import type { PropsWithChildren } from 'react';

/**
 * Filters the properties of a given object into two categories: data-attributes and other properties
 * Data attributes are properties that start with 'data-'.
 * Other properties are all the remaining properties
 *
 * @param props - The properties to be filtered.
 * @returns An array containing two objects: the first object contains the data attributes, and the second object contains the other properties.
 *
 * @usage
 * ```ts
 * const props = {
 *  'data-foo': 'foo',
 *  'data-bar': 'bar',
 *  'aria-baz': 'baz',
 *  };
 *
 * const [dataAttributes, otherProps] = useFilteredDataAttributes(props);
 * console.log(dataAttributes); // { 'data-foo': 'foo', 'data-bar': 'bar' }
 * ```
 */

export const useFilteredDataAttributes = <T = unknown>(
  props: PropsWithChildren<T>
) => {
  const dataAttributes: Record<string, unknown> = {};
  const otherProps: Record<string, unknown> = {};

  Object.entries(props).forEach(([key, value]) => {
    if (key.startsWith(`data-`)) {
      dataAttributes[key] = value;
    } else {
      otherProps[key] = value;
    }
  });

  return [dataAttributes, otherProps];
};
